import { useState, type FormEvent } from "react";
import { Link, Navigate } from "react-router-dom";
import { HCaptchaField } from "../components/HCaptchaField";
import { useCustomer } from "../customer/CustomerContext";
import {
  emailMessage,
  isValidEmail,
  requiredMessage,
} from "../utils/formValidation";

type ForgotResponse = {
  success?: boolean;
  message?: string;
  expires_in_seconds?: number;
  expires_at?: string;
  dev_reset_url?: string;
  dev_otp?: string;
  error?: string;
  field_errors?: Record<string, string>;
};

export function ForgotPasswordPage() {
  const { customer } = useCustomer();
  const [email, setEmail] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaReset, setCaptchaReset] = useState(0);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<ForgotResponse | null>(null);
  const [loading, setLoading] = useState(false);

  if (customer) return <Navigate to="/account" replace />;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!email.trim()) errs.email = requiredMessage("Email");
    else if (!isValidEmail(email)) errs.email = emailMessage();
    setFieldErrors(errs);
    if (Object.keys(errs).length) {
      setError("Please fill in the required fields.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const res = await fetch("/api/customers/forgot-password", {
        method: "POST",
        headers: { Accept: "application/json", "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim(),
          h_captcha_response: captchaToken || undefined,
        }),
      });
      const data = (await res.json().catch(() => ({}))) as ForgotResponse;
      if (!res.ok) {
        if (data.field_errors) setFieldErrors(data.field_errors);
        throw new Error(data.error || "Request failed.");
      }
      setDone(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Request failed.");
      setCaptchaToken("");
      setCaptchaReset((n) => n + 1);
    } finally {
      setLoading(false);
    }
  }

  return (
    <article className="page account-page">
      <p className="eyebrow">Customer account</p>
      <h1>Forgot password</h1>
      <p className="page-summary">
        Enter your email and we will send a reset link with a one-time code (OTP). The OTP
        expires after a few minutes.
      </p>
      {done ? (
        <div className="account-reset-done">
          <p role="status">{done.message || "Check your email for the reset link and OTP."}</p>
          {done.dev_reset_url ? (
            <p className="page-summary">
              Email sending is disabled locally.{" "}
              <Link to={done.dev_reset_url.replace(/^https?:\/\/[^/]+/, "")}>
                Open reset page
              </Link>
              {done.dev_otp ? (
                <>
                  {" "}
                  — OTP: <code>{done.dev_otp}</code>
                </>
              ) : null}
            </p>
          ) : null}
          <p className="account-switch">
            <Link to="/account/login">Back to log in</Link>
          </p>
        </div>
      ) : (
        <>
          <form className="deposit-form account-form" onSubmit={onSubmit} noValidate>
            <div className="buy-server-row">
              <label htmlFor="forgot-email">
                Email <span className="req-star">*</span>
              </label>
              <div className="buy-server-control">
                <input
                  id="forgot-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  aria-invalid={Boolean(fieldErrors.email)}
                  onChange={(e) => setEmail(e.target.value)}
                />
                {fieldErrors.email ? <p className="field-error">{fieldErrors.email}</p> : null}
              </div>
            </div>
            <HCaptchaField purpose="login" onToken={setCaptchaToken} resetKey={captchaReset} />
            {fieldErrors.captcha ? <p className="field-error">{fieldErrors.captcha}</p> : null}
            {error ? (
              <p className="model-run-error" role="alert">
                {error}
              </p>
            ) : null}
            <div className="buy-server-actions">
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? "Sending…" : "Send reset link"}
              </button>
            </div>
          </form>
          <p className="account-switch">
            Remembered it? <Link to="/account/login">Log in</Link>
          </p>
        </>
      )}
    </article>
  );
}
